/**
 * 场景级视频生成 Panel · 基于已选故事板 + 场景剧情信息
 *
 * 数据:scene.videoRuns / scene.selectedVideoUrl
 * 任务态:本地 pollTaskId + GET /director/tasks/{id} 5s 轮询
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Film, Loader2, Play, RefreshCw, Trash2, X } from 'lucide-react';
import { api, extractApiMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { InlineLightbox } from '@/pages/brain/Lightbox';
import type {
  DirectorSceneShots,
  DirectorTaskSnapshot,
  SceneVideoGenerateResponse,
  SceneVideoRun,
  SceneVideoSelectResponse,
} from './types';

type Resolution = '480p' | '720p';
type Duration = number;
type EndingFrameOffset = number;

const DURATION_OPTIONS: Duration[] = Array.from({ length: 11 }, (_, i) => i + 5);
const ENDING_FRAME_OFFSET_OPTIONS: EndingFrameOffset[] = Array.from({ length: 10 }, (_, i) => (i + 1) * 0.5);
const NO_DIALOGUE_SIMPLE_MAX_SEC = 2.2;
const NO_DIALOGUE_INTERACTION_MAX_SEC = 3.0;

function shotBlockHasVoice(blockText: string): boolean {
  const noVoiceMarkers = ['无对白', '无台词', '无对话', '无OS', '无旁白'];
  const voiceFieldRe = /^(?:剧情\/对白|对白|台词|旁白|OS|VO|内心独白)\s*[:：]\s*(.*)$/i;
  const inlineVoiceRe = /(?:OS|VO|旁白|画外音|台词|说|问|喊|答|念|读|低声|耳语|喃喃|嘟囔|吼|电话那头|画外).*?[“"']|[“"'][^“”"']{2,}[”"']/i;
  for (const raw of blockText.split(/\r?\n/)) {
    const line = raw.trim();
    if (noVoiceMarkers.some((marker) => line.includes(marker))) continue;
    const match = line.match(voiceFieldRe);
    if (match?.[1]?.trim()) return true;
    if (inlineVoiceRe.test(line)) return true;
  }
  return false;
}

function shotBlockNeedsPerformanceRoom(blockText: string): boolean {
  const keywords = [
    '画外', '电话那头', '听筒', '传来', '说话声', '喊声', '哭声', '笑声',
    '对视', '凝视', '僵住', '沉默', '停顿', '迟疑', '压迫',
    '靠近', '后退', '转身', '起身', '坐下', '跪下', '摔倒',
    '抓住', '握住', '拉住', '推开', '推搡', '拉扯', '拦住', '挡住',
    '递给', '接过', '抢过', '夺过', '抱住', '拥抱', '扶住', '搀扶',
    '拍肩', '触碰', '躲开', '挣脱', '扇', '打', '挥拳', '撞上',
  ];
  return keywords.some((kw) => blockText.includes(kw));
}

function noDialogueDurationCap(blockText: string): number {
  return shotBlockNeedsPerformanceRoom(blockText)
    ? NO_DIALOGUE_INTERACTION_MAX_SEC
    : NO_DIALOGUE_SIMPLE_MAX_SEC;
}

function shotNoFromPrefix(prefix: string | undefined): number | null {
  const raw = prefix?.match(/\d+/)?.[0];
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function durationFromCameraPrompt(cameraPrompt: string | null | undefined): Duration {
  const text = cameraPrompt?.trim();
  if (!text) return 15;
  const re = /((?:镜头\s*\d+|镜(?!头)\s*\d+)\s*[（(]\s*)(?:约\s*)?(\d+(?:\.\d+)?)\s*[sS秒]?\s*[）)]/g;
  const matches = Array.from(text.matchAll(re));
  let total = 0;
  for (const [idx, match] of matches.entries()) {
    const dur = Number(match[2]);
    if (!Number.isFinite(dur) || dur <= 0) continue;
    const start = match.index ?? 0;
    const end = matches[idx + 1]?.index ?? text.length;
    const blockText = text.slice(start, end);
    const shotNo = shotNoFromPrefix(match[1]);
    const cap = shotNo === 0 ? 0.5 : shotBlockHasVoice(blockText) ? 15 : noDialogueDurationCap(blockText);
    total += Math.min(dur, cap);
  }
  if (total <= 0) return 15;
  return Math.min(15, Math.max(5, Math.ceil(total))) as Duration;
}

interface Props {
  episodeId: string;
  episodeIndex: number;
  sceneId: string;
  scene: DirectorSceneShots;
  usePrevEndingFrame: boolean;
}

export function SceneVideoGachaPanel({
  episodeId,
  episodeIndex,
  sceneId,
  scene,
  usePrevEndingFrame,
}: Props) {
  const qc = useQueryClient();
  const completedTaskIds = useRef<Set<string>>(new Set());
  const suggestedDuration = useMemo(
    () => durationFromCameraPrompt(scene.cameraPrompt),
    [scene.cameraPrompt],
  );
  const [resolution, setResolution] = useState<Resolution>('480p');
  const [duration, setDuration] = useState<Duration>(suggestedDuration);
  const lastSuggestedDurationRef = useRef<Duration>(suggestedDuration);
  const [endingFrameLightbox, setEndingFrameLightbox] = useState<string | null>(null);

  useEffect(() => {
    setDuration((prev) => {
      const shouldSync = prev === lastSuggestedDurationRef.current;
      lastSuggestedDurationRef.current = suggestedDuration;
      return shouldSync ? suggestedDuration : prev;
    });
  }, [suggestedDuration]);

  // 从 server 恢复正在运行的视频任务
  const runningRun = (scene.videoRuns ?? []).find(
    (r) => r.status === 'queued' || r.status === 'running',
  );
  const [pollTaskId, setPollTaskId] = useState<string | null>(
    runningRun?.taskId ?? null,
  );

  // 从 server 同步任务状态(场景切换 / plan 轮询刷新时)
  useEffect(() => {
    const activeRun = (scene.videoRuns ?? []).find(
      (r) => r.status === 'queued' || r.status === 'running',
    );
    if (
      activeRun?.taskId &&
      !pollTaskId &&
      !completedTaskIds.current.has(activeRun.taskId)
    ) {
      setPollTaskId(activeRun.taskId);
    }
  }, [scene.videoRuns, pollTaskId]);

  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  useEffect(() => {
    if (!armedDeleteId) return;
    const t = setTimeout(() => setArmedDeleteId(null), 3000);
    return () => clearTimeout(t);
  }, [armedDeleteId]);

  const basePath = `director/episodes/${episodeId}/director/episodes/${episodeIndex}/scenes/${sceneId}/video`;

  const generateMut = useMutation({
    mutationFn: async () =>
      api
        .post(`${basePath}/generate`, { json: { resolution, duration, usePrevEndingFrame } })
        .json<SceneVideoGenerateResponse>(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setPollTaskId(res.taskId);
      toast.success('已下发场景视频任务', '约 10 分钟完成');
    },
    onError: (e: Error) => toast.error('视频生成失败', extractApiMessage(e)),
  });

  const taskQuery = useQuery({
    queryKey: ['director', 'task', pollTaskId],
    queryFn: () =>
      api
        .get(`director/tasks/${pollTaskId}`)
        .json<DirectorTaskSnapshot>(),
    enabled: !!pollTaskId,
    refetchInterval: (q) => {
      const s = (q.state.data as DirectorTaskSnapshot | undefined)?.status;
      if (!s) return 5000;
      return s === 'queued' || s === 'running' ? 5000 : false;
    },
  });

  useEffect(() => {
    if (!pollTaskId) return;
    const s = taskQuery.data?.status;
    if (s && s !== 'queued' && s !== 'running') {
      completedTaskIds.current.add(pollTaskId);
      setPollTaskId(null);
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      if (s === 'failed') {
        toast.error('视频生成失败', extractApiMessage(taskQuery.data?.error));
      }
    }
  }, [taskQuery.data?.status, taskQuery.data?.error, pollTaskId, qc, episodeId]);

  const selectMut = useMutation({
    mutationFn: async ({
      runId,
      endingFrameOffsetSec,
    }: {
      runId: string;
      endingFrameOffsetSec: EndingFrameOffset;
    }) =>
      api
        .post(`${basePath}/select`, { json: { runId, endingFrameOffsetSec } })
        .json<SceneVideoSelectResponse>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已切换为该视频');
    },
    onError: (e: Error) => toast.error('切换失败', e.message || '请重试'),
  });

  const deselectMut = useMutation({
    mutationFn: async () =>
      api
        .delete(`${basePath}/deselect`)
        .json<{ sceneId: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已取消选定');
    },
    onError: (e: Error) => toast.error('取消失败', e.message || '请重试'),
  });

  const deleteMut = useMutation({
    mutationFn: async (runId: string) =>
      api
        .delete(`${basePath}/${runId}`)
        .json<{ sceneId: string; deletedRunId: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已删除该视频');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const runs = [...(scene.videoRuns ?? [])].reverse();
  const hasCameraPrompt = !!(scene.cameraPrompt?.trim());
  const canGenerate = hasCameraPrompt;
  const hasRunningRun = (scene.videoRuns ?? []).some(
    (r) => r.status === 'queued' || r.status === 'running',
  );
  const busy = !!pollTaskId || hasRunningRun || generateMut.isPending;

  const recoverableRuns = (scene.videoRuns ?? []).filter((r) => {
    if (!r.taskId || !r.externalTaskId) return false;
    if (r.status === 'failed') return true;
    if (r.status === 'queued' || r.status === 'running') {
      const startedMs = r.createdAt ? Date.parse(r.createdAt) : NaN;
      return Number.isFinite(startedMs) && Date.now() - startedMs > 10 * 60 * 1000;
    }
    return false;
  });

  const [recovering, setRecovering] = useState(false);
  async function handleRecover() {
    if (recovering || recoverableRuns.length === 0) return;
    setRecovering(true);
    try {
      await Promise.all(
        recoverableRuns.map((r) =>
          api
            .post(`${basePath}/reconcile`, { json: { runId: r.id } })
            .json()
            .catch(() => null),
        ),
      );
      await qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    } finally {
      setRecovering(false);
    }
  }

  return (
    <section className="border-t border-outline-variant/20 bg-neutral-900/[0.02] px-3 py-2.5">
      <header className="mb-2 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          <Film className="h-3 w-3 text-brand-500" /> 场景视频生成
        </h4>
        <div className="flex items-center gap-1.5">
          {recoverableRuns.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRecover}
              disabled={recovering}
              title="向 Seedance 核对任务是否已完成(用于卡住或失败的任务)"
            >
              {recovering ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              检查是否已完成
            </Button>
          )}
          <ResolutionToggle
            value={resolution}
            onChange={setResolution}
            disabled={busy}
          />
          <DurationToggle
            value={duration}
            onChange={setDuration}
            suggestedDuration={suggestedDuration}
            disabled={busy}
          />
          <Button
            variant="agent"
            agentColor="director"
            size="sm"
            onClick={() => generateMut.mutate()}
            disabled={!canGenerate || busy}
            title={
              !canGenerate
                ? '请先生成分镜镜头提示词'
                : undefined
            }
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
            生成视频
          </Button>
        </div>
      </header>

      {!canGenerate && (
        <p className="mb-2 rounded border border-dashed border-amber-200 bg-amber-50/40 px-2 py-1 text-[11px] text-amber-700">
          请先生成分镜镜头提示词
        </p>
      )}

      {canGenerate && (
        <p className="mb-2 rounded border border-dashed border-sky-200 bg-sky-50/40 px-2 py-1 text-[11px] leading-snug text-sky-700">
          基于分镜镜头提示词 + 场景剧情 + 影视风格生成视频
        </p>
      )}

      {(scene.endingContinuityFrameUrl || scene.endingFrameUrl) && (
        <div className="mb-2 flex items-center gap-2 rounded border border-violet-200 bg-violet-50/40 px-2.5 py-2">
          <div className="text-[10px] font-medium text-violet-600 shrink-0">
            {scene.endingContinuityFrameUrl ? '安全承接帧' : '承接帧'}
            <span className="ml-1 font-normal text-violet-400">
              倒数 {scene.endingFrameOffsetSec ?? 0.5}s
            </span>
          </div>
          <button
            type="button"
            onClick={() => setEndingFrameLightbox((scene.endingContinuityFrameUrl || scene.endingFrameUrl)!)}
            className="h-10 w-10 shrink-0 overflow-hidden rounded border border-violet-200 bg-neutral-100 cursor-pointer hover:ring-2 hover:ring-violet-300 transition"
          >
            <img
              src={(scene.endingContinuityFrameUrl || scene.endingFrameUrl)!}
              alt="承接帧"
              className="h-full w-full object-cover"
            />
          </button>
          {scene.endingRawFrameUrl && scene.endingRawFrameUrl !== (scene.endingContinuityFrameUrl || scene.endingFrameUrl) && (
            <button
              type="button"
              onClick={() => setEndingFrameLightbox(scene.endingRawFrameUrl!)}
              className="rounded border border-violet-200 bg-white/70 px-1.5 py-0.5 text-[10px] text-violet-500 transition hover:bg-violet-100"
              title="查看未处理的原始截图"
            >
              原图
            </button>
          )}
          <p className="text-[10px] leading-snug text-violet-500">
            {scene.endingContinuityFrameUrl
              ? '已按安全策略处理为低真人风险承接帧,仅在勾选衔接上镜时用于下一场开头'
              : '安全处理未开启,仅在勾选衔接上镜时使用原始截图作为开头承接参考'}
          </p>
        </div>
      )}

      {runs.length === 0 ? (
        <p className="rounded border border-dashed border-outline-variant/40 px-3 py-4 text-center text-[11px] text-neutral-400">
          尚未生成视频
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {runs.map((r) => (
            <SceneVideoCard
              key={r.id}
              run={r}
              selected={scene.selectedVideoUrl === r.videoUrl && !!r.videoUrl}
              onSelect={(endingFrameOffsetSec) => r.videoUrl && selectMut.mutate({ runId: r.id, endingFrameOffsetSec })}
              onDeselect={() => deselectMut.mutate()}
              disabled={selectMut.isPending}
              selecting={selectMut.isPending && selectMut.variables?.runId === r.id}
              armed={armedDeleteId === r.id}
              onArmDelete={() => setArmedDeleteId(r.id)}
              onConfirmDelete={() => {
                setArmedDeleteId(null);
                deleteMut.mutate(r.id);
              }}
              deleting={deleteMut.isPending && deleteMut.variables === r.id}
            />
          ))}
        </div>
      )}

      <InlineLightbox
        url={endingFrameLightbox}
        alt="结束帧"
        onClose={() => setEndingFrameLightbox(null)}
      />
    </section>
  );
}

function ResolutionToggle({
  value,
  onChange,
  disabled,
}: {
  value: Resolution;
  onChange: (v: Resolution) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-outline-variant/40 bg-surface text-[11px]">
      {(['480p', '720p'] as Resolution[]).map((v) => (
        <button
          key={v}
          type="button"
          disabled={disabled}
          onClick={() => onChange(v)}
          className={`px-2 py-0.5 transition ${
            value === v
              ? 'bg-brand-500 text-white'
              : 'text-neutral-600 hover:bg-surface-variant/40'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function DurationToggle({
  value,
  onChange,
  suggestedDuration,
  disabled,
}: {
  value: Duration;
  onChange: (v: Duration) => void;
  suggestedDuration: Duration;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value) as Duration)}
      title={`视频时长(秒)，默认按分镜镜头时长之和向上取整：${suggestedDuration}s`}
      className={`rounded-md border border-outline-variant/40 bg-surface px-2 py-0.5 text-[11px] text-neutral-700 outline-none transition focus:border-brand-400 ${
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-brand-300'
      }`}
    >
      {DURATION_OPTIONS.map((v) => (
        <option key={v} value={v}>
          {v}s
        </option>
      ))}
    </select>
  );
}

function EndingFrameOffsetToggle({
  value,
  onChange,
  disabled,
}: {
  value: EndingFrameOffset;
  onChange: (v: EndingFrameOffset) => void;
  disabled?: boolean;
}) {
  return (
    <label className="inline-flex items-center gap-1 text-[10px] text-neutral-500">
      承接帧
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        title="选定视频时截取倒数第几秒作为下一场开头承接帧"
        className={`rounded border border-outline-variant/40 bg-surface px-1.5 py-0.5 text-[10px] text-neutral-700 outline-none transition focus:border-brand-400 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-brand-300'
        }`}
      >
        {ENDING_FRAME_OFFSET_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v}s
          </option>
        ))}
      </select>
    </label>
  );
}

function SceneVideoCard({
  run,
  selected,
  onSelect,
  onDeselect,
  disabled,
  selecting,
  armed,
  onArmDelete,
  onConfirmDelete,
  deleting,
}: {
  run: SceneVideoRun;
  selected: boolean;
  onSelect: (endingFrameOffsetSec: EndingFrameOffset) => void;
  onDeselect: () => void;
  disabled?: boolean;
  selecting?: boolean;
  armed: boolean;
  onArmDelete: () => void;
  onConfirmDelete: () => void;
  deleting?: boolean;
}) {
  const isRunning = run.status === 'queued' || run.status === 'running';
  const isFailed = run.status === 'failed';
  const ar = run.aspectRatio || '16:9';
  const [endingFrameOffsetSec, setEndingFrameOffsetSec] = useState<EndingFrameOffset>(0.5);
  return (
    <div
      className={`overflow-hidden rounded-md border bg-surface transition ${
        selected
          ? 'border-brand-500 ring-2 ring-brand-300'
          : 'border-outline-variant/30'
      }`}
    >
      <style>{`
        @keyframes sf-pending-grad {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div
        className={`relative w-full ${run.videoUrl ? 'bg-neutral-900' : ''}`}
        style={{ aspectRatio: ar.replace(':', '/') }}
      >
        {run.videoUrl ? (
          <video
            src={run.videoUrl}
            controls
            playsInline
            className="h-full w-full"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center text-[11px] text-brand-700"
            style={
              isRunning
                ? {
                    background:
                      'linear-gradient(135deg, #fdf2f8 0%, #ede9fe 25%, #dbeafe 50%, #ccfbf1 75%, #fef9c3 100%)',
                    backgroundSize: '300% 300%',
                    animation: 'sf-pending-grad 6s ease-in-out infinite',
                  }
                : isFailed
                  ? { background: '#fef2f2' }
                  : { background: '#f5f5f4' }
            }
          >
            {isRunning ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> 渲染中…
              </span>
            ) : isFailed ? (
              <span className="px-3 text-center text-red-600">
                生成失败 · {run.error ?? '未知错误'}
              </span>
            ) : (
              <span className="text-neutral-500">排队中</span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-1 border-t border-outline-variant/20 bg-surface px-2 py-1 text-[10px] text-neutral-500">
        <span>
          {run.resolution} · {ar}
        </span>
        <div className="flex items-center gap-1">
          {run.videoUrl && (
            selected ? (
              <button
                type="button"
                onClick={onDeselect}
                disabled={disabled}
                className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 transition border border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <X className="h-2.5 w-2.5" />
                取消选定
              </button>
            ) : (
              <>
                <EndingFrameOffsetToggle
                  value={endingFrameOffsetSec}
                  onChange={setEndingFrameOffsetSec}
                  disabled={disabled}
                />
                <button
                  type="button"
                  onClick={() => onSelect(endingFrameOffsetSec)}
                  disabled={disabled}
                  className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 transition border border-outline-variant/40 text-neutral-600 hover:bg-brand-50 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  title={`设为本场景,并截取倒数 ${endingFrameOffsetSec}s 作为结束关键帧`}
                >
                  {selecting ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Check className="h-2.5 w-2.5" />}
                  设为本场景
                </button>
              </>
            )
          )}
          <button
            type="button"
            onClick={() => {
              if (armed) onConfirmDelete();
              else onArmDelete();
            }}
            disabled={deleting}
            className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-white transition disabled:opacity-50 ${
              armed
                ? 'bg-rose-600 ring-1 ring-rose-300 animate-pulse'
                : 'bg-rose-500/80 hover:bg-rose-600'
            }`}
            title={armed ? '再点确认删除' : '删除'}
          >
            {deleting ? (
              <Loader2 className="h-2.5 w-2.5 animate-spin" />
            ) : (
              <Trash2 className="h-2.5 w-2.5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
